import { isFixedProduct, isPromotionProduct, isUsageProduct } from '@vy/services';
import type { FixedProductInput, UsageProductInput } from '@vy/types';
import { CONTRACT_TYPES } from '@vy/types';
import { isNotNullOrUndefined, oz } from '@vy/utils';
import React, { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import useFormPersist from 'react-hook-form-persist';
import { ErrorLabel } from '../../../../components/design/ErrorLabel';
import { withSuspenseBoundary } from '../../../../components/utility/SuspenseBoundary';
import { LocalStorageKeys } from '../../../../constants/local-storage-keys';
import { useCreatePlanContext } from '../../../../contexts/create-plan-context';
import { PLAN_CREATION_STEPS } from '../../../../contexts/create-plan-context/create-plan-context-types/plan-creation-step';
import { useContract } from '../../../../hooks/requests/use-contract';
import { useValidatedParams } from '../../../../hooks/use-validated-params';
import { getProRataSetting } from '../../../../services/get-pro-ration-setting-value';
import { processRateMultiplierPlanProducts } from '../../../../services/rate-multiplier-product-processor';
import type { NewPlanForm } from '../../../plans/CreatePlanPage/create-plan-page-components/CreatePlanForm/create-plan-form-types/new-plan-form';
import {
  PlanInputFormSchema,
  type PlanInputForm,
} from '../../../plans/CreatePlanPage/create-plan-page-components/CreatePlanForm/create-plan-form-types/plan-input-form';
import { getPlanSettingsInputFormSchema } from '../../../plans/CreatePlanPage/create-plan-page-components/CreatePlanForm/create-plan-form-types/plan-settings-input-form';
import { getCreatePlanDefaultValues } from '../../../plans/CreatePlanPage/create-plan-page-consts/create-plan-default-values';
import { createDiscountStrippingResolver } from '../../../../utils/strip-incomplete-discounts';

export const UpdateContractFormSchema = oz.object({
  startDate: oz.date(),
});

export type UpdateContractFormType = oz.infer<typeof UpdateContractFormSchema>;

const UpdateContractForm: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { contractId } = useValidatedParams('contractId');

  const {
    data: { contract },
    isSuccess,
  } = useContract(contractId, {
    refetchOnWindowFocus: false,
  });

  const {
    planCreationStepper: { currentStep },
  } = useCreatePlanContext();

  const isParentAgreement = contract?.contractType === CONTRACT_TYPES.ParentAgreement;
  const entityFormSchema = getPlanSettingsInputFormSchema(false, isParentAgreement);
  const activeFormSchema =
    currentStep === PLAN_CREATION_STEPS.settings ? entityFormSchema : PlanInputFormSchema;

  const methods = useForm<PlanInputForm, unknown, NewPlanForm>({
    resolver: createDiscountStrippingResolver(activeFormSchema),
    defaultValues: getCreatePlanDefaultValues({
      startDate: contract?.startDate,
      customerId: contract?.customer?.id,
      signatureDate: contract?.signatureDate,
      contractType: contract?.contractType,
    }),
  });

  useFormPersist(LocalStorageKeys.UpdateContract, {
    watch: methods.watch,
    setValue: methods.setValue,
    storage: window.localStorage,
  });

  useEffect(() => {
    if (isSuccess) {
      const { products: planProducts, ...planWithoutProducts } = contract.plan;
      const rateAdjustedProducts = processRateMultiplierPlanProducts(planProducts);
      const filteredFixedProductsWithPromotions = rateAdjustedProducts.filter(
        isFixedProduct,
      ) as FixedProductInput[];
      const filteredFixedProducts = filteredFixedProductsWithPromotions.filter(
        (product) => !isPromotionProduct(product),
      );
      const promotionProducts = filteredFixedProductsWithPromotions.filter((product) =>
        isPromotionProduct(product),
      );
      const filteredUsageProducts = (rateAdjustedProducts.filter(isUsageProduct) as UsageProductInput[]).map(
        (product) =>
          product.commitment
            ? {
                ...product,
                commitment: {
                  ...product.commitment,
                  autoRefillEnabled: isNotNullOrUndefined(product.commitment.replenishmentInterval),
                },
              }
            : product,
      );

      const defaultValues = {
        ...planWithoutProducts,
        proRataSetting: getProRataSetting(contract.plan),
        startDate: contract.startDate,
        customerId: contract.customer.id,
        currencyOverride: contract.currencyOverride,
        contractType: contract.contractType,
        fixedProducts: filteredFixedProducts,
        usageProducts: filteredUsageProducts,
        promotionProducts,
      };

      methods.reset(defaultValues);
    }
  }, [isSuccess, contract.plan, contract.currencyOverride, contract.contractType, methods]);

  return (
    <FormProvider {...methods}>
      <form className="flex h-full flex-col">{children}</form>
      {isNotNullOrUndefined(methods.formState.errors.root) && (
        <ErrorLabel errorMessage={methods.formState.errors.root?.message ?? ''} />
      )}
    </FormProvider>
  );
};

const UpdatePlanPageWithSuspenseBoundary = withSuspenseBoundary(UpdateContractForm);
export { UpdatePlanPageWithSuspenseBoundary as UpdateContractForm };
